import { Box, Flex, Heading, SimpleGrid, useToast } from '@chakra-ui/react';
import axios from 'axios';
import { subDays } from 'date-fns';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import DatePickerBase from '../../../../../components/Form/DatePickerBase';
import { useAuth } from '../../../../../hooks/auth';
import { listBookingCancellationHistoriesService } from '../../../../../services/Bookings/ListBookingCancellationHistoriesService';
import { maskDateTime } from '../../../../../utils/formatters/handleMask';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import {
  BookingCancellationHistoryTable,
  IBookingCancellationHistoryTableItem,
} from './components/BookingCancellationHistoryTable';

interface ILoadBookingCancellationHistories {
  maxDate?: Date;
  minDate?: Date;
  userDocument?: string;
  userName?: string;
  sort?: 'minutesBeforeScheduleStart' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const BookingCancellationHistoryList = (): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [bookingCancellationHistoryList, setBookingCancellationHistoryList] =
    useState<IBookingCancellationHistoryTableItem[]>([]);

  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('userName');
  const [sortBy, setSortBy] = useState<
    'minutesBeforeScheduleStart' | 'createdAt'
  >('createdAt');
  const [totalPages, setTotalPages] = useState<number>();
  const [currentMinCancellationDate, setcurrentMinCancellationDate] = useState<
    Date | undefined
  >(subDays(new Date(), 1));
  const [currentMaxCancellationDate, setcurrentMaxCancellationDate] = useState<
    Date | undefined
  >(new Date());
  const [currentFilters, setCurrentFilters] = useState<
    { [key: string]: string } | undefined
  >(() => {
    const response: {
      [key: string]: string;
    } = {};

    if (currentMaxCancellationDate) {
      response.maxDate = currentMaxCancellationDate.toISOString();
    }

    if (currentMinCancellationDate) {
      response.minDate = currentMinCancellationDate.toISOString();
    }

    return response;
  });

  const loadBookingCancellationHistories = useCallback(
    async ({
      order,
      sort,
      userDocument,
      userName,
      maxDate,
      minDate,
      page = 1,
    }: ILoadBookingCancellationHistories) => {
      try {
        const { items: cancellationHistories, pages } =
          await listBookingCancellationHistoriesService({
            order,
            sort,
            userDocument: userDocument?.replace(/\D/g, ''),
            userName,
            limit: 20,
            maxDate,
            minDate,
            page,
          });

        const parsedCancellationHistories = cancellationHistories.map(
          (cancellationHistory) => {
            const [bookedYear, bookedMonth, bookedDay] =
              cancellationHistory.booking.bookedDate.split('-');

            return {
              ...cancellationHistory,
              formattedBookedDate: `${bookedDay}/${bookedMonth}/${bookedYear}`,
              formattedCreatedAt: maskDateTime(cancellationHistory.createdAt),
              formattedEndTime:
                cancellationHistory.booking.activitySchedule.endTime.slice(
                  0,
                  5,
                ),
              formattedStartTime:
                cancellationHistory.booking.activitySchedule.startTime.slice(
                  0,
                  5,
                ),
            };
          },
        );

        setTotalPages(pages);
        setBookingCancellationHistoryList(parsedCancellationHistories);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              'Ocorreu um erro ao carregar os dados dos cancelamentos, tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setBookingCancellationHistoryList([]);

    loadBookingCancellationHistories({
      ...currentFilters,
      maxDate: currentMaxCancellationDate,
      minDate: currentMinCancellationDate,
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    authenticatedVenture,
    currentPage,
    loadBookingCancellationHistories,
    currentOrdering,
    currentFilters,
    sortBy,
    currentMaxCancellationDate,
    currentMinCancellationDate,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );
  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'minutesBeforeScheduleStart' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <Flex justify="space-between" align="center">
          <Heading size="lg" fontWeight="normal">
            Cancelamentos
          </Heading>
        </Flex>

        <SimpleGrid mt="4" minChildWidth="120px" spacing="4" maxW={320}>
          <DatePickerBase
            size="sm"
            name="cancelled-since"
            label="Cancelado de"
            maxDate={currentMaxCancellationDate}
            isClearable
            selectsStart
            startDate={currentMinCancellationDate}
            endDate={currentMaxCancellationDate}
            onChangeDate={setcurrentMinCancellationDate}
            selectedDate={currentMinCancellationDate}
          />

          <DatePickerBase
            size="sm"
            name="cancelled-until"
            label="Cancelado até"
            maxDate={new Date()}
            isClearable
            selectsEnd
            startDate={currentMinCancellationDate}
            endDate={currentMaxCancellationDate}
            onChangeDate={setcurrentMaxCancellationDate}
            selectedDate={currentMaxCancellationDate}
          />
        </SimpleGrid>

        <BookingCancellationHistoryTable
          mt="4"
          cancellationHistories={bookingCancellationHistoryList}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
        />
      </Box>
    </DefaultLayout>
  );
};
